// Small building blocks for the chrome: buttons, fields, segmented controls,
// swatches, sliders, switches, the dialog shell, and the fades of a box that scrolls.

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cx } from "../lib/util";
import { savedVersion, watchHolder, type Holder } from "../state/store";

export function IconButton({ label, active, onClick, children, className, kbd, disabled }: { label: string; active?: boolean; onClick?: (e: React.MouseEvent) => void; children: ReactNode; className?: string; kbd?: string; disabled?: boolean }) {
  return (
    <button className={cx("icon-btn", active && "is-active", className)} aria-label={label} aria-pressed={active} data-tip={kbd ? `${label}  ${kbd}` : label} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

export function Label({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="label">
      <span>{children}</span>
      {aside}
    </div>
  );
}

export function Segmented<T extends string>({ value, options, onChange, size = "md" }: { value: T; options: { value: T; label: ReactNode; title?: string }[]; onChange: (v: T) => void; size?: "sm" | "md" }) {
  return (
    <div className={cx("segmented", `seg-${size}`)} role="radiogroup">
      {options.map((o) => (
        <button key={o.value} role="radio" aria-checked={o.value === value} className={cx(o.value === value && "is-on")} title={o.title} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Swatches({ value, colors, onChange, allowCustom = true, onPreview }: { value: string | null | undefined; colors: string[]; onChange: (c: string) => void; allowCustom?: boolean; onPreview?: (c: string | null) => void }) {
  return (
    <div className="swatches" onPointerLeave={() => onPreview?.(null)}>
      {colors.map((c) => (
        <button
          key={c}
          className={cx("swatch", value?.toLowerCase() === c.toLowerCase() && "is-on")}
          style={{ background: c }}
          aria-label={c}
          onPointerEnter={() => onPreview?.(c)}
          onFocus={() => onPreview?.(c)}
          onBlur={() => onPreview?.(null)}
          onClick={() => {
            onPreview?.(null);
            onChange(c);
          }}
        />
      ))}
      {allowCustom && (
        <label className="swatch swatch-custom" title="Any color">
          <input type="color" value={value && /^#[0-9a-f]{6}$/i.test(value) ? value : "#ffffff"} onChange={(e) => onChange(e.target.value)} />
        </label>
      )}
    </div>
  );
}

export function Slider({ value, min, max, step = 1, onChange, format }: { value: number; min: number; max: number; step?: number; onChange: (v: number) => void; format?: (v: number) => string }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="slider">
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ "--pct": `${pct}%` } as React.CSSProperties} />
      <span className="slider-value">{format ? format(value) : value}</span>
    </div>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode }) {
  return (
    <label className="switch-row">
      {label && <span>{label}</span>}
      <button role="switch" aria-checked={checked} className={cx("switch", checked && "is-on")} onClick={() => onChange(!checked)}>
        <i />
      </button>
    </label>
  );
}

export function Field({ label, children, hint }: { label: ReactNode; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

/** A field that holds words: a text box, a list to choose from, editable text. Not a switch, a slider or a color well. */
function fieldOf(t: EventTarget | null): HTMLElement | null {
  const el = t as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") return null;
  if (el.tagName === "TEXTAREA" || el.tagName === "SELECT") return el;
  if (el.tagName === "INPUT") return /^(checkbox|radio|range|color|file|button|submit|reset|hidden|image)$/.test((el as HTMLInputElement).type) ? null : el;
  return el.isContentEditable ? el : null;
}
const wordsIn = (el: HTMLElement) => ("value" in el ? String((el as HTMLInputElement).value) : (el.textContent ?? ""));

/**
 * What someone has typed in the fields inside one dialog or panel, for the store's guard (state/store.ts
 * keepsPlace). A field holds unsaved words once it was typed in and its words differ from what it held
 * before, while it is still on the page. They are saved once its form is sent, or once the field saves
 * itself: its own handler changes what the board holds as it lets go (a name saved on blur), or the page
 * sets a list it was choosing from back at once, because the choice lives in what is being saved.
 */
export class Typed implements Holder {
  private before = new Map<HTMLElement, string>();
  private typed = new Set<HTMLElement>();
  /** what the board held when a field began to let go, to tell whether its own handler saved it */
  private mark = new Map<HTMLElement, number>();
  /** the user's events that passed through this dialog or panel on their way */
  private seen = new WeakSet<Event>();
  /** the dialog or panel itself */
  private root: HTMLElement | null = null;
  focus(target: EventTarget | null) {
    const el = fieldOf(target);
    if (el && !this.typed.has(el)) this.before.set(el, wordsIn(el));
  }
  input(target: EventTarget | null) {
    const el = fieldOf(target);
    if (!el) return;
    // a field typed in before it had the focus here: what the page last drew in it
    if (!this.before.has(el)) this.before.set(el, "defaultValue" in el ? String((el as HTMLInputElement).defaultValue) : "");
    this.typed.add(el);
  }
  submit(form: EventTarget | null) {
    for (const el of [...this.typed]) if ((form as Node | null)?.contains?.(el)) this.saved(el);
  }
  /** A field begins to let go of its words (blur) or takes a choice (change), before its own handler runs. */
  leaving(target: EventTarget | null) {
    const el = fieldOf(target);
    if (el && this.typed.has(el)) this.mark.set(el, savedVersion());
  }
  /** And after its own handler ran: if that changed what the board holds, the field saved itself. */
  left(target: EventTarget | null) {
    const el = fieldOf(target);
    if (!el || !this.typed.has(el)) return;
    if (this.mark.has(el) && this.mark.get(el) !== savedVersion()) this.saved(el);
    this.mark.delete(el);
  }
  /** A choice made in a list: if the page sets the list back at once, the choice went to what is being saved (a store picked for a key). */
  chose(target: EventTarget | null) {
    const el = fieldOf(target);
    if (!el || el.tagName !== "SELECT" || !this.typed.has(el)) return;
    const chosen = wordsIn(el);
    // after the page has drawn the field again, once this event is through
    queueMicrotask(() => {
      if (wordsIn(el) !== chosen) this.saved(el);
    });
  }
  /** A user's event on its way through this dialog or panel; where it starts, the place to show it stayed. */
  saw(e: Event, root: EventTarget | null) {
    this.seen.add(e);
    if (root && "isConnected" in root) this.root = root as HTMLElement;
  }
  private saved(el: HTMLElement) {
    this.typed.delete(el);
    this.before.set(el, wordsIn(el));
  }
  holds(): boolean {
    for (const el of this.typed) if (el.isConnected && wordsIn(el) !== this.before.get(el)) return true;
    return false;
  }
  /** The user acting in it: a click, a choice or a form inside it, Enter in one of its fields. Not a shortcut key pressed with the focus on one of its buttons. */
  acting(e: Event): boolean {
    if (!this.seen.has(e)) return false;
    return e.type === "keydown" || e.type === "keyup" ? fieldOf(e.target) !== null : true;
  }
  /** A small shake, or a fade where motion is turned down: it stayed, words and all. */
  nudge() {
    const el = this.root;
    if (!el?.isConnected || typeof el.animate !== "function") return;
    const still = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.animate(still ? [{ opacity: 1 }, { opacity: 0.55 }, { opacity: 1 }] : [{ translate: "0 0" }, { translate: "-7px 0" }, { translate: "6px 0" }, { translate: "-3px 0" }, { translate: "0 0" }], { duration: still ? 420 : 320, easing: "ease-out" });
  }
}

/** Counts what is typed in these fields for the store's guard, until the function it returns is called. */
export function watchTyped(typed: Typed): () => void {
  return watchHolder(typed);
}

/** The user's events a dialog or panel notes on their way through it, before anything inside it acts on them. */
const SEEN = ["onClickCapture", "onPointerDownCapture", "onPointerUpCapture", "onMouseDownCapture", "onMouseUpCapture", "onKeyDownCapture", "onKeyUpCapture", "onSubmitCapture", "onChangeCapture", "onInputCapture", "onPasteCapture", "onDropCapture"] as const;

/** Notes what is typed inside an element, for the store's guard, while it is on the page. Spread it on the element around the fields. */
export function useUnsaved() {
  const typed = useMemo(() => new Typed(), []);
  useEffect(() => watchTyped(typed), [typed]);
  return useMemo(() => {
    const saw = (e: React.SyntheticEvent) => typed.saw(e.nativeEvent, e.currentTarget);
    return {
      ...(Object.fromEntries(SEEN.map((k) => [k, saw])) as Record<(typeof SEEN)[number], typeof saw>),
      onFocus: (e: React.FocusEvent) => typed.focus(e.target),
      onInput: (e: React.FormEvent) => typed.input(e.target),
      onSubmit: (e: React.FormEvent) => typed.submit(e.target),
      onBlurCapture: (e: React.FocusEvent) => typed.leaving(e.target),
      onBlur: (e: React.FocusEvent) => typed.left(e.target),
      onChangeCapture: (e: React.FormEvent) => {
        saw(e);
        typed.leaving(e.target);
      },
      onChange: (e: React.FormEvent) => {
        typed.left(e.target);
        typed.chose(e.target);
      },
    };
  }, [typed]);
}

export function Dialog({ open, onClose, title, children, width = 640, className, footer, icon, fieldFocus = true }: { open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode; width?: number; className?: string; footer?: ReactNode; icon?: ReactNode; /** false: the dialog itself takes focus, never its first field, so a phone's keyboard waits for a tap in it */ fieldFocus?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const unsaved = useUnsaved();
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => {
      // a small layer open inside the dialog (a card over a tile) takes Escape first, and the dialog stays
      if (e.key === "Escape" && !(e.target instanceof Element && e.target.closest("[data-esc-own]"))) {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", k, true);
    const prev = document.activeElement as HTMLElement | null;
    // a field that took focus on its own keeps it (React's autoFocus never
    // writes the attribute), and the close button in the header never gets it
    setTimeout(() => {
      const root = ref.current;
      if (!root || root.contains(document.activeElement)) return;
      const target = fieldFocus ? (root.querySelector<HTMLElement>(".dialog-body input:not([type=hidden]), .dialog-body textarea, .dialog-foot .btn-primary, .dialog-body button") ?? root) : root;
      target.focus({ preventScroll: !fieldFocus });
    }, 10);
    return () => {
      window.removeEventListener("keydown", k, true);
      prev?.focus?.();
    };
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="dialog-scrim" onPointerDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={cx("dialog", className)} style={{ width }} role="dialog" aria-modal aria-label={typeof title === "string" ? title : undefined} tabIndex={-1} {...unsaved}>
        {title && (
          <div className="dialog-head">
            {icon && <span className="dialog-icon">{icon}</span>}
            <h2>{title}</h2>
            <button className="icon-btn sm" onClick={onClose} aria-label="Close">
              <X size={15} />
            </button>
          </div>
        )}
        <div className="dialog-body">{children}</div>
        {footer && <div className="dialog-foot">{footer}</div>}
      </div>
    </div>,
    document.querySelector(".board-root") ?? document.body,
  );
}

/**
 * Whether a box scrolls, and which of its ends have more past them, as
 * "is-scroll has-above has-below", for the fades. Measured again whenever
 * `again` changes, for content that grows without the box changing size.
 */
export function useScrollEnds(ref: React.RefObject<HTMLElement | null>, again?: unknown): string {
  const [ends, setEnds] = useState("");
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const over = el.scrollHeight > el.clientHeight + 1;
      setEnds(over ? cx("is-scroll", el.scrollTop > 1 && "has-above", el.scrollTop + el.clientHeight < el.scrollHeight - 1 && "has-below") : "");
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [ref, again]);
  return ends;
}
